import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
// @mui
import Stack from '@mui/material/Stack';
import Popover from '@mui/material/Popover';
import { appBarClasses } from '@mui/material/AppBar';
// routes
import { usePathname } from '@/routes/hooks';
import { useActiveLink } from '@/routes/hooks/use-active-link';
//
import { NavListProps, NavConfigProps } from '../types';
import NavItem from './nav-item';

// ----------------------------------------------------------------------

type NavListRootProps = {
  data: NavListProps;
  depth: number;
  hasChild: boolean;
  config: NavConfigProps;
};

export default function NavList({ data, depth, hasChild, config }: NavListRootProps) {
  const navRef = useRef(null);

  const pathname = usePathname();

  const active = useActiveLink(data.path, hasChild);

  const externalLink = useMemo(() => data.path.includes('http'), [data.path]);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    const appBarEl = Array.from(
      document.querySelectorAll(`.${appBarClasses.root}`)
    ) as Array<HTMLElement>;

    // Reset styles when hover
    const styles = () => {
      document.body.style.overflow = '';
      document.body.style.padding = '';
      // Apply for Window
      appBarEl.forEach((elem) => {
        elem.style.padding = '';
      });
    };

    if (open) {
      styles();
    } else {
      styles();
    }
  }, [open]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (open) {
      handleClose();
    }
  }, [open, pathname, handleClose]);

  const navItemProps = useMemo(
    () => ({
      ref: navRef,
      item: data,
      depth,
      open,
      active,
      externalLink,
      onClick: handleToggle,
      onMouseEnter: handleOpen,
      onMouseLeave: handleClose,
      config,
    }),
    [data, depth, open, active, externalLink, handleToggle, handleOpen, handleClose, config]
  );

  const popoverProps = useMemo(
    () => ({
      open,
      anchorEl: navRef.current,
      anchorOrigin: { vertical: 'center' as const, horizontal: 'right' as const },
      transformOrigin: { vertical: 'center' as const, horizontal: 'left' as const },
      slotProps: {
        paper: {
          onMouseEnter: handleOpen,
          onMouseLeave: handleClose,
          sx: {
            mt: 0.5,
            width: 160,
            ...(open && {
              pointerEvents: 'auto',
            }),
          },
        },
      },
      sx: {
        pointerEvents: 'none',
      },
    }),
    [open, handleOpen, handleClose]
  );

  return (
    <>
      <NavItem {...navItemProps} />

      {hasChild && (
        <Popover {...popoverProps}>
          <NavSubList data={data.children} depth={depth} config={config} />
        </Popover>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

type NavListSubProps = {
  data: NavListProps[];
  depth: number;
  config: NavConfigProps;
};

function NavSubList({ data, depth, config }: NavListSubProps) {
  const renderItems = useMemo(
    () =>
      data.map((list) => (
        <NavList
          key={list.title + list.path}
          data={list}
          depth={depth + 1}
          hasChild={!!list.children}
          config={config}
        />
      )),
    [data, depth, config]
  );

  return <Stack spacing={0.5}>{renderItems}</Stack>;
}
